import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Building2,
  Search,
  Check,
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
  Info,
  Plus,
  Edit2,
  ChevronDown,
  ArrowRight,
} from "lucide-react";
import { toast } from "react-toastify";
import organizerService from "../../../../../api/OrganizerService";
import CreateEditOrganizerModal from "./CreateEditOrganizerModal";

/**
 * Step Two: Organizer Selection Component
 * Reusable organizer selection component (same as events)
 */
const StepTwoOrganizerSelection = ({ selectedOrganizer, onSelect, onNext, onBack }) => {
  const [organizers, setOrganizers] = useState([]);
  const [filteredOrganizers, setFilteredOrganizers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [organizerToEdit, setOrganizerToEdit] = useState(null);

  const ITEMS_PER_PAGE = 24;
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);

  // Fetch organizers function
  const fetchOrganizers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await organizerService.getOrganizers();
      if (response.data.status_code === 200) {
        const organizersList = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data || [];
        setOrganizers(organizersList);
        setFilteredOrganizers(organizersList);
        return organizersList;
      } else {
        setError(response.data.reason || "Failed to load organizers");
        toast.error(response.data.reason || "Failed to load organizers");
        return [];
      }
    } catch (err) {
      const errorMessage = "An error occurred while loading organizers";
      setError(errorMessage);
      toast.error(errorMessage);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizers();
  }, [fetchOrganizers]);

  // Filter organizers based on search query
  useEffect(() => {
    if (organizers.length > 0 && !searchQuery.trim()) {
      setFilteredOrganizers(organizers);
      setDisplayedCount(ITEMS_PER_PAGE);
    } else if (searchQuery.trim()) {
      const filtered = organizers.filter(
        (org) =>
          org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          org.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          org.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          org.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          org.country?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredOrganizers(filtered);
      setDisplayedCount(ITEMS_PER_PAGE);
    }
  }, [searchQuery, organizers]);

  // Get displayed organizers
  const displayedOrganizers = useMemo(() => {
    return filteredOrganizers.slice(0, displayedCount);
  }, [filteredOrganizers, displayedCount]);

  const hasMore = displayedCount < filteredOrganizers.length;

  const handleLoadMore = () => {
    setDisplayedCount((prev) => prev + ITEMS_PER_PAGE);
  };

  const handleSelect = (organizer) => {
    onSelect(organizer);
  };

  const handleCreateNew = () => {
    setOrganizerToEdit(null);
    setIsModalOpen(true);
  };

  const handleEdit = (organizer, e) => {
    e.stopPropagation();
    setOrganizerToEdit(organizer);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setOrganizerToEdit(null);
  };

  const handleModalSuccess = () => {
    fetchOrganizers();
    handleModalClose();
  };

  const getDefaultOrganizerLogo = (organizer) => {
    const initials = (organizer.name || "O")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
    return (
      <span className="text-lg font-bold text-white">{initials}</span>
    );
  };

  const handleNext = () => {
    if (selectedOrganizer) {
      onNext();
    } else {
      toast.error("Please select an organizer");
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        <p className="mt-4 text-sm text-gray-600">Loading organizers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <p className="mt-4 text-sm font-medium text-gray-900">{error}</p>
        <button
          onClick={fetchOrganizers}
          className="mt-4 px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Select Organizer</h2>
          <p className="mt-1 text-sm text-gray-600">
            Choose the organizer hosting this event
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg shadow-sm transition-all hover:shadow-md"
        >
          <Plus className="mr-2 w-4 h-4" />
          Add New
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search organizers by name, email, phone, city, or country..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {/* Organizers Grid */}
      {displayedOrganizers.length === 0 ? (
        <div className="py-12 text-center">
          <Building2 className="mx-auto w-12 h-12 text-gray-400" />
          <p className="mt-4 text-sm font-medium text-gray-900">
            No organizers found
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {searchQuery
              ? "Try adjusting your search"
              : "Create a new organizer to get started"}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {displayedOrganizers.map((organizer) => {
              const isSelected =
                selectedOrganizer?.slug === organizer.slug;
              return (
                <div
                  key={organizer.slug}
                  onClick={() => handleSelect(organizer)}
                  className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    isSelected
                      ? "border-purple-500 bg-purple-50 shadow-md"
                      : "border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm"
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <div className="p-1 bg-purple-600 rounded-full">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}

                  <div className="flex items-start space-x-3">
                    <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg shadow-sm">
                      {organizer.logo ? (
                        <img
                          src={organizer.logo}
                          alt={organizer.name}
                          className="object-cover w-full h-full"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : null}
                      {!organizer.logo && getDefaultOrganizerLogo(organizer)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 truncate">
                        {organizer.name}
                      </h3>
                      <div className="mt-1 space-y-1">
                        {organizer.email && (
                          <div className="flex items-center text-xs text-gray-600">
                            <Mail className="mr-1 w-3 h-3" />
                            <span className="truncate">{organizer.email}</span>
                          </div>
                        )}
                        {organizer.phone && (
                          <div className="flex items-center text-xs text-gray-600">
                            <Phone className="mr-1 w-3 h-3" />
                            <span>{organizer.phone}</span>
                          </div>
                        )}
                        {(organizer.city || organizer.country) && (
                          <div className="flex items-center text-xs text-gray-600">
                            <MapPin className="mr-1 w-3 h-3" />
                            <span className="truncate">
                              {organizer.city}
                              {organizer.city && organizer.country && ", "}
                              {organizer.country}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleEdit(organizer, e)}
                      className="flex-shrink-0 p-1.5 text-gray-400 rounded-lg transition-colors hover:text-purple-600 hover:bg-purple-50"
                      title="Edit organizer"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center pt-4">
              <button
                onClick={handleLoadMore}
                className="flex items-center px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100"
              >
                Load More
                <ChevronDown className="ml-2 w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          onClick={onBack}
          className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!selectedOrganizer}
          className="flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg shadow-sm transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
          <ArrowRight className="ml-2 w-4 h-4" />
        </button>
      </div>

      {/* Create/Edit Organizer Modal */}
      <CreateEditOrganizerModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        organizer={organizerToEdit}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default StepTwoOrganizerSelection;

